<template>
    <div>
        <v-menu :nudge-width="100" offset-y v-if="user" flat>
            <template v-slot:activator="{ on, attrs }">
                <div class="d-flex align-center" v-bind="attrs" v-on="on">
                    <div
                        v-if="!$vuetify.breakpoint.mobile"
                        class="pl-2 font-medium"
                    >
                        {{ user.fullName }}
                    </div>
                    <v-avatar
                        color="pyramir"
                        v-if="user.pictureProfileUrl"
                        size="36"
                    >
                        <v-img
                            :src="getFullImageUrl(user.pictureProfileUrl)"
                            width="90"
                        ></v-img>
                    </v-avatar>
                    <v-avatar
                        v-else
                        size="36"
                        color="primary"
                        class="white--text font-weight-bold"
                    >
                        <span>{{
                            user?.fullName?.split(" ")[0]?.charAt(0)
                        }}</span>
                        <span>{{
                            user?.fullName?.split(" ")[1]
                                ? user?.fullName?.split(" ")[1]?.charAt(0)
                                : ""
                        }}</span>
                    </v-avatar>
                </div>
            </template>

            <v-card flat class="text-center pa-10">
                <v-avatar size="90" class="mx-auto mb-3">
                    <img
                        v-if="user.pictureProfileUrl"
                        :src="getFullImageUrl(user.pictureProfileUrl)"
                        :alt="user.fullName"
                    />
                    <v-img
                        v-else
                        src="@/assets/img/user.png"
                        alt="user"
                    ></v-img>
                </v-avatar>
                <h3 class="text-center mt-4 primary--text">
                    {{ user.fullName }}
                </h3>
                <v-card-text>
                    <div class="primary--text mt-n2 text-decoration-underline">
                        {{ user.userName }}
                    </div>
                    <div class="grey--text mt-n2">{{ user.email }}</div>
                    <v-btn
                        @click="openEditProfileDialog"
                        text
                        block
                        color="primary"
                        class="my-2"
                    >
                        <v-icon left>mdi-account-edit</v-icon>
                        تعديل المعلومات الشخصية
                    </v-btn>
                </v-card-text>

                <v-divider></v-divider>

                <v-btn block outlined color="error" @click="logOut()"
                    >تسجيل خروج</v-btn
                >
            </v-card>
        </v-menu>

        <!-- Edit Profile Dialog -->
        <v-dialog v-model="editProfileDialog" max-width="800" persistent>
            <v-card>
                <v-card-title class="primary white--text">
                    <v-icon left color="white">mdi-account-edit</v-icon>
                    تعديل المعلومات الشخصية
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="closeEditProfileDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="pa-4 overflow-auto" style="height: 60vh">
                    <v-form
                        ref="profileForm"
                        v-model="formValid"
                        lazy-validation
                    >
                        <v-row>
                            <!-- Profile Picture Section -->
                            <v-col cols="12" class="text-center">
                                <div class="mb-3">
                                    <v-avatar size="120" class="mb-3">
                                        <img
                                            v-if="
                                                profileForm.pictureProfile ||
                                                currentProfilePictureUrl
                                            "
                                            :src="
                                                profileForm.pictureProfile ||
                                                getFullImageUrl(
                                                    currentProfilePictureUrl
                                                )
                                            "
                                            :alt="profileForm.fullName"
                                        />
                                        <v-icon v-else size="80" color="primary"
                                            >mdi-account</v-icon
                                        >
                                    </v-avatar>
                                </div>
                                <v-file-input
                                    v-model="profileImageFile"
                                    @change="handleImageUpload"
                                    accept="image/*"
                                    prepend-icon="mdi-camera"
                                    label="صورة الملف الشخصي"
                                    show-size
                                    truncate-length="15"
                                    outlined
                                    dense
                                ></v-file-input>
                            </v-col>

                            <!-- Full Name -->
                            <v-col cols="12" md="6">
                                <h6 class="mb-1">
                                    الاسم الكامل
                                    <span class="required">*</span>
                                </h6>
                                <v-text-field
                                    v-model="profileForm.fullName"
                                    :rules="[rules.required]"
                                    outlined
                                    dense
                                    placeholder="الاسم الكامل"
                                ></v-text-field>
                            </v-col>

                            <!-- Email -->
                            <v-col cols="12" md="6">
                                <h6 class="mb-1">البريد الإلكتروني</h6>
                                <v-text-field
                                    v-model="profileForm.email"
                                    :rules="[rules.emailFormat]"
                                    outlined
                                    dense
                                    placeholder="example@domain.com"
                                    dir="ltr"
                                ></v-text-field>
                            </v-col>

                            <!-- Province -->
                            <v-col cols="12" md="6">
                                <h6 class="mb-1">المحافظة</h6>
                                <v-autocomplete
                                    v-model="profileForm.provinceId"
                                    :items="$global.state.provinces"
                                    item-text="name"
                                    item-value="id"
                                    outlined
                                    dense
                                    placeholder="اختر المحافظة"
                                    no-data-text="لا توجد بيانات"
                                    prepend-inner-icon="mdi-map-marker"
                                    clearable
                                ></v-autocomplete>
                            </v-col>

                            <!-- Birth Date -->
                            <v-col cols="12" md="6">
                                <h6 class="mb-1">تاريخ الميلاد</h6>
                                <v-menu
                                    v-model="birthDateMenu"
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="profileForm.dateBirth"
                                            prepend-inner-icon="mdi-calendar"
                                            readonly
                                            v-bind="attrs"
                                            v-on="on"
                                            outlined
                                            dense
                                            placeholder="اختر تاريخ الميلاد"
                                            clearable
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="profileForm.dateBirth"
                                        @input="birthDateMenu = false"
                                    ></v-date-picker>
                                </v-menu>
                            </v-col>

                            <!-- Full Address -->
                            <v-col cols="12">
                                <h6 class="mb-1">العنوان الكامل</h6>
                                <v-textarea
                                    v-model="profileForm.fullAddress"
                                    outlined
                                    dense
                                    placeholder="العنوان الكامل"
                                    rows="3"
                                    auto-grow
                                ></v-textarea>
                            </v-col>

                            <!-- Notes -->
                            <v-col cols="12">
                                <h6 class="mb-1">ملاحظات</h6>
                                <v-textarea
                                    v-model="profileForm.notes"
                                    outlined
                                    dense
                                    placeholder="ملاحظات إضافية"
                                    rows="3"
                                    auto-grow
                                ></v-textarea>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <v-card-actions class="pa-4">
                    <v-btn
                        color="primary"
                        :loading="formSubmitting"
                        :disabled="!formValid"
                        @click="updateProfile"
                    >
                        <v-icon left>mdi-content-save</v-icon>
                        حفظ التعديلات
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeEditProfileDialog">إلغاء</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>
<script>
import { mapState } from "vuex";
import axios from "@/axios";
import config from "@/../public/static/config.json";

export default {
    data() {
        return {
            dailogchangePassword: false,
            user: null,

            // Edit Profile Dialog
            editProfileDialog: false,
            formValid: false,
            formSubmitting: false,
            birthDateMenu: false,
            profileImageFile: null,
            currentProfilePictureUrl: null,

            // Profile Form Data
            profileForm: {
                fullName: "",
                email: "",
                pictureProfile: "",
                provinceId: null,
                fullAddress: "",
                notes: "",
                dateBirth: "",
            },

            // Provinces List
            provinces: [
                { id: 1, name: "بغداد" },
                { id: 2, name: "البصرة" },
                { id: 3, name: "نينوى" },
                { id: 4, name: "أربيل" },
                { id: 5, name: "النجف" },
                { id: 6, name: "كربلاء" },
                { id: 7, name: "بابل" },
                { id: 8, name: "الديوانية" },
                { id: 9, name: "ذي قار" },
                { id: 10, name: "المثنى" },
                { id: 11, name: "كركوك" },
                { id: 12, name: "الأنبار" },
                { id: 13, name: "صلاح الدين" },
                { id: 14, name: "ديالى" },
                { id: 15, name: "ميسان" },
                { id: 16, name: "واسط" },
                { id: 17, name: "دهوك" },
                { id: 18, name: "السليمانية" },
            ],

            // Form Validation Rules
            rules: {
                required: (value) => !!value || "هذا الحقل مطلوب",
                emailFormat: (value) => {
                    if (!value) return true;
                    const pattern =
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                    return pattern.test(value) || "البريد الإلكتروني غير صحيح";
                },
            },
        };
    },

    computed: {
        ...mapState("auth", ["userData"]),

        baseUrl() {
            return process.env.NODE_ENV === "production"
                ? config.APP_URL_PROD
                : config.APP_URL_DEV;
        },
    },

    methods: {
        // Profile Edit Methods
        async openEditProfileDialog() {
            this.editProfileDialog = true;
            await this.getMyProfileInfo();
        },

        async getMyProfileInfo() {
            try {
                const response = await axios.get("/Auth/myProfileInfo");

                if (response.data.statusCode === 200) {
                    const profileData = response.data.result;
                    this.populateProfileForm(profileData);
                } else {
                    this.$toast.error("حدث خطأ أثناء تحميل البيانات");
                    // Fallback to current user data
                    this.populateProfileForm(this.user);
                }
            } catch (error) {
                console.error("Error fetching profile info:", error);
                this.$toast.error("حدث خطأ أثناء تحميل البيانات");
                // Fallback to current user data
                this.populateProfileForm(this.user);
            }
        },

        closeEditProfileDialog() {
            this.editProfileDialog = false;
            this.resetProfileForm();
            this.$refs.profileForm?.resetValidation();
        },

        populateProfileForm(profileData) {
            // Use profileData if provided, otherwise fall back to this.user
            const data = profileData || this.user;

            // Store the current profile picture URL
            this.currentProfilePictureUrl = data.pictureProfileUrl || null;

            this.profileForm = {
                fullName: data.fullName || "",
                email: data.email || "",
                pictureProfile: "",
                provinceId: data.province
                    ? data.province.id
                    : data.provinceId || null,
                fullAddress: data.fullAddress || "",
                notes: data.notes || "",
                dateBirth: data.dateBirth ? data.dateBirth.split("T")[0] : "",
            };
        },

        resetProfileForm() {
            this.profileForm = {
                fullName: "",
                email: "",
                pictureProfile: "",
                provinceId: null,
                fullAddress: "",
                notes: "",
                dateBirth: "",
            };
            this.profileImageFile = null;
        },

        handleImageUpload() {
            if (this.profileImageFile) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.profileForm.pictureProfile = e.target.result;
                };
                reader.readAsDataURL(this.profileImageFile);
            }
        },

        getFullImageUrl(pictureUrl) {
            if (!pictureUrl) return null;
            // If the URL is already a full URL (starts with http), return as is
            if (pictureUrl.startsWith("http")) {
                return pictureUrl;
            }
            // Otherwise, prepend the base URL
            return `${this.baseUrl}/${pictureUrl}`;
        },

        async updateProfile() {
            if (!this.$refs.profileForm.validate()) {
                return;
            }

            this.formSubmitting = true;
            try {
                const fd = new FormData();

                // Required fields
                if (this.profileForm.fullName) {
                    fd.append("FullName", this.profileForm.fullName);
                }

                // Optional fields - only append if they have values
                if (this.profileForm.email) {
                    fd.append("Email", this.profileForm.email);
                }

                // Picture Profile - use the actual file if uploaded
                if (this.profileImageFile) {
                    fd.append("PictureProfile", this.profileImageFile);
                }

                // Province ID - convert to string if exists
                if (this.profileForm.provinceId) {
                    fd.append(
                        "ProvinceId",
                        this.profileForm.provinceId.toString()
                    );
                }

                // Optional text fields
                if (this.profileForm.fullAddress) {
                    fd.append("FullAddress", this.profileForm.fullAddress);
                }
                if (this.profileForm.notes) {
                    fd.append("Notes", this.profileForm.notes);
                }

                // Date Birth
                if (this.profileForm.dateBirth) {
                    fd.append("DateBirth", this.profileForm.dateBirth);
                }

                const response = await axios.put("/Admin/my-profile", fd, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });

                if (
                    response.data.statusCode === 200 ||
                    response.data.statusCode === 201
                ) {
                    this.$toast.success("تم تحديث المعلومات الشخصية بنجاح");

                    // Update the user data in the store
                    this.user = { ...this.user, ...this.profileForm };
                    this.$store.commit("auth/updateUserData", this.user);

                    this.closeEditProfileDialog();
                } else {
                    // Handle validation errors
                    if (response.data.result) {
                        if (Array.isArray(response.data.result)) {
                            response.data.result.forEach((error) => {
                                this.$toast.warning(error);
                            });
                        } else if (typeof response.data.result === "string") {
                            this.$toast.warning(response.data.result);
                        }
                    } else if (response.data.message) {
                        this.$toast.warning(response.data.message);
                    } else {
                        this.$toast.error("حدث خطأ أثناء تحديث المعلومات");
                    }
                }
            } catch (error) {
                console.error("Error updating profile:", error);
                if (error.response && error.response.data) {
                    if (error.response.data.result) {
                        if (Array.isArray(error.response.data.result)) {
                            error.response.data.result.forEach((errorMsg) => {
                                this.$toast.warning(errorMsg);
                            });
                        } else if (
                            typeof error.response.data.result === "string"
                        ) {
                            this.$toast.warning(error.response.data.result);
                        }
                    } else if (error.response.data.message) {
                        this.$toast.warning(error.response.data.message);
                    } else {
                        this.$toast.error("حدث خطأ أثناء تحديث المعلومات");
                    }
                } else {
                    this.$toast.error("حدث خطأ أثناء تحديث المعلومات");
                }
            } finally {
                this.formSubmitting = false;
            }
        },

        // Existing Methods
        async logOut() {
            this.$store.dispatch("auth/logout");
            this.$store.dispatch("toastification/setToast", {
                message: `تم تسجيل الخروج`,
            });
            this.$router.push("/login");
        },

        async unsubscribeFromTopic() {
            this.$messaging()
                .unsubscribeFromTopic("registrationTokens", "admin")
                .then(function (response) {
                    // See the MessagingTopicManagementResponse reference documentation
                    // for the contents of response.
                    console.log(
                        "Successfully unsubscribed from topic:",
                        response
                    );
                })
                .catch(function (error) {
                    console.log("Error unsubscribing from topic:", error);
                });
        },

        // Get user info from API
        async getUserInfo() {
            try {
                const response = await axios.get("/Auth/myProfileInfo");

                if (response.data.statusCode === 200) {
                    this.user = response.data.result;
                } else {
                    // Fallback to store data if API fails
                    this.user = this.userData.userData;
                }
            } catch (error) {
                console.error("Error fetching user info:", error);
                // Fallback to store data if API fails
                this.user = this.userData.userData;
            }
        },
    },
    created() {
        if (this.userData.accessToken) {
            this.getUserInfo();
        }
        // console.log(this.user);
    },
};
</script>

<style scoped>
.required {
    color: red;
    font-weight: bold;
}
</style>
